/**
 * Shared invoice header layout (client mirror).
 * Canonical copy: account_api/worker/src/pdf/header/layout.js
 *
 * Preview and PDF must call the same functions so WYSIWYG holds.
 * Placement box is inset on all four sides of the logo column.
 */

import { type HeaderTemplateSpec, getHeaderTemplateSpec, resolvePdfTemplateId } from "./templates";

export const SCALE_MIN = 0.1;
export const SCALE_MAX = 1;

/** Horizontal nudge + scale only — logo is always bottom-stuck inside the placement box. */
export interface LogoPlacement {
  scale: number;
  offset_x: number;
}

export interface MetaLineHeightSpec {
  size: number;
  bold?: boolean;
}

export function defaultPlacement(p?: Partial<LogoPlacement>): LogoPlacement {
  return clampPlacement(p || {});
}

export function clampPlacement(p: Partial<LogoPlacement>): LogoPlacement {
  return {
    scale: Math.max(SCALE_MIN, Math.min(SCALE_MAX, Number(p.scale) || 1)),
    offset_x: Math.max(-1, Math.min(1, Number(p.offset_x) || 0)),
  };
}

/** Meta block height in PDF points — mirrors classic.js metaY loop. */
export function computeMetaBlockHeight(metaLines: MetaLineHeightSpec[], topInsetPt = 0): number {
  let total = topInsetPt;
  for (const line of metaLines) {
    total += line.size + (line.bold ? 10 : 4);
  }
  return total;
}

export function computeLogoDrawSize(
  sourceW: number,
  sourceH: number,
  columnWidthPt: number,
  placement: Partial<LogoPlacement>,
  logoInsetLeftPt = 0,
  logoInsetRightPt = 0
): { widthPt: number; heightPt: number } {
  if (!sourceW || !sourceH || !columnWidthPt) {
    return { widthPt: 0, heightPt: 0 };
  }
  const p = clampPlacement(placement);
  const innerW = Math.max(1, columnWidthPt - logoInsetLeftPt - logoInsetRightPt);
  const widthFit = innerW / sourceW;
  const widthPt = sourceW * widthFit * p.scale;
  const heightPt = sourceH * widthFit * p.scale;
  return { widthPt, heightPt };
}

export function computeLogoXInColumn(
  columnWidthPt: number,
  logoWidthPt: number,
  placement: Partial<LogoPlacement>,
  logoInsetLeftPt = 0,
  logoInsetRightPt = 0
): number {
  const p = clampPlacement(placement);
  const innerW = Math.max(1, columnWidthPt - logoInsetLeftPt - logoInsetRightPt);
  const centered = logoInsetLeftPt + (innerW - logoWidthPt) / 2;
  let x = centered + p.offset_x * innerW;
  const maxX = Math.max(logoInsetLeftPt, columnWidthPt - logoInsetRightPt - logoWidthPt);
  x = Math.min(Math.max(logoInsetLeftPt, x), maxX);
  return x;
}

export interface HeaderLayoutResult {
  leftColumnWidthPt: number;
  leftX: number;
  metaColumnX: number;
  metaHeightPt: number;
  logoWidthPt: number;
  logoHeightPt: number;
  logoXInColumn: number;
  logoXPt: number;
  logoInsetLeftPt: number;
  logoInsetRightPt: number;
  logoInsetTopPt: number;
  logoInsetBottomPt: number;
  metaInsetLeftPt: number;
  metaInsetRightPt: number;
  metaInsetTopPt: number;
  metaInsetBottomPt: number;
  metaRightPt: number;
  headerBandHeightPt: number;
  dividerGapPt: number;
  dividerHeightPt: number;
}

export function computeHeaderLayout(params: {
  contentWidthPt: number;
  pageMarginPt: number;
  spec: HeaderTemplateSpec;
  metaHeightPt: number;
  sourceWidthPx?: number;
  sourceHeightPx?: number;
  placement?: Partial<LogoPlacement>;
  logoEnabled?: boolean;
}): HeaderLayoutResult {
  const {
    contentWidthPt,
    pageMarginPt,
    spec,
    metaHeightPt,
    sourceWidthPx = 0,
    sourceHeightPx = 0,
    placement = {},
    logoEnabled = false,
  } = params;

  const leftColumnWidthPt = contentWidthPt * spec.slotFraction;
  const leftX = pageMarginPt;
  const metaColumnX = pageMarginPt + leftColumnWidthPt;
  const logoInsetLeftPt = spec.logoInsetLeftPt ?? 0;
  const logoInsetRightPt = spec.logoInsetRightPt ?? 0;
  const logoInsetTopPt = spec.logoInsetTopPt ?? 0;
  const logoInsetBottomPt = spec.logoInsetBottomPt ?? 0;
  const metaInsetLeftPt = spec.metaInsetLeftPt ?? 0;
  const metaInsetRightPt = spec.metaInsetRightPt ?? 0;
  const metaInsetTopPt = spec.metaInsetTopPt ?? 0;
  const metaInsetBottomPt = spec.metaInsetBottomPt ?? 0;

  let logoWidthPt = 0;
  let logoHeightPt = 0;
  let logoXInColumn = 0;

  if (logoEnabled && sourceWidthPx > 0 && sourceHeightPx > 0) {
    const size = computeLogoDrawSize(
      sourceWidthPx,
      sourceHeightPx,
      leftColumnWidthPt,
      placement,
      logoInsetLeftPt,
      logoInsetRightPt
    );
    logoWidthPt = size.widthPt;
    logoHeightPt = size.heightPt;
    logoXInColumn = computeLogoXInColumn(
      leftColumnWidthPt,
      logoWidthPt,
      placement,
      logoInsetLeftPt,
      logoInsetRightPt
    );
  }

  const logoBandPt =
    logoEnabled && logoHeightPt > 0
      ? logoInsetTopPt + logoHeightPt + logoInsetBottomPt
      : 0;
  const metaBandPt = metaInsetTopPt + metaHeightPt + metaInsetBottomPt;
  const headerBandHeightPt = Math.max(logoBandPt, metaBandPt, spec.minHeaderHeightPt);

  return {
    leftColumnWidthPt,
    leftX,
    metaColumnX,
    metaHeightPt,
    logoWidthPt,
    logoHeightPt,
    logoXInColumn,
    logoXPt: leftX + logoXInColumn,
    logoInsetLeftPt,
    logoInsetRightPt,
    logoInsetTopPt,
    logoInsetBottomPt,
    metaInsetLeftPt,
    metaInsetRightPt,
    metaInsetTopPt,
    metaInsetBottomPt,
    metaRightPt: pageMarginPt + contentWidthPt - metaInsetRightPt,
    headerBandHeightPt,
    dividerGapPt: spec.dividerGapPt,
    dividerHeightPt: spec.dividerHeightPt,
  };
}

/** PDF page constants — keep in sync with account_api/worker/src/pdf/geometry.js */
export const PDF_PAGE_WIDTH_PT = 595.28;
export const PDF_PAGE_MARGIN_PT = 56.69;
export const PDF_CONTENT_WIDTH_PT = PDF_PAGE_WIDTH_PT - 2 * PDF_PAGE_MARGIN_PT;

export function resolveLogoDataUrl(business: Record<string, unknown>): string {
  const source = String(business.logo_source_b64 || "").trim();
  if (source) return source;
  return String(business.logo_b64 || "").trim();
}

export function resolveLogoPlacement(
  business: Record<string, unknown>,
  templateId = "classic"
): LogoPlacement {
  const byTemplate = business.logo_placement_by_template as
    | Record<string, Partial<LogoPlacement>>
    | undefined;
  if (byTemplate && typeof byTemplate === "object") {
    const raw = byTemplate[templateId];
    if (raw && typeof raw === "object") {
      return clampPlacement(raw);
    }
  }
  const legacy = business.logo_placement as Partial<LogoPlacement> | undefined;
  if (legacy && typeof legacy === "object") {
    return clampPlacement({ scale: legacy.scale, offset_x: legacy.offset_x });
  }
  return defaultPlacement();
}

export function isLogoEnabledForBusiness(business: Record<string, unknown>): boolean {
  if (business.logo_enabled === false) return false;
  return Boolean(resolveLogoDataUrl(business));
}

export function placementForSave(
  placement: LogoPlacement,
  templateId: string
): Record<string, unknown> {
  const clamped = clampPlacement(placement);
  return {
    logo_placement_by_template: {
      [templateId]: clamped,
    },
    logo_placement: clamped,
  };
}

export function computePreviewLayout(
  business: Record<string, unknown>,
  metaLines: MetaLineHeightSpec[],
  sourceWidthPx: number,
  sourceHeightPx: number,
  placementOverride?: Partial<LogoPlacement>
) {
  const templateId = resolvePdfTemplateId(business);
  const spec = getHeaderTemplateSpec(templateId);
  const metaHeightPt = computeMetaBlockHeight(metaLines, spec.metaTopInsetPt);
  const placement = placementOverride
    ? clampPlacement({ ...resolveLogoPlacement(business, templateId), ...placementOverride })
    : resolveLogoPlacement(business, templateId);
  const logoEnabled = Boolean(sourceWidthPx && sourceHeightPx);

  const layout = computeHeaderLayout({
    contentWidthPt: PDF_CONTENT_WIDTH_PT,
    pageMarginPt: PDF_PAGE_MARGIN_PT,
    spec,
    metaHeightPt,
    sourceWidthPx,
    sourceHeightPx,
    placement,
    logoEnabled,
  });

  return { templateId, spec, placement, layout };
}
